import { useState } from "react";
import { FaGoogle } from "react-icons/fa";
import blueWave from "../../assets/blue_wave.mp4";
import { useAuthActions } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { AuthModalLayout } from "./components/AuthModalLayout";

export function AuthModal({
  isShown,
  canClose,
  additionalBtn,
  onClose,
}: {
  isShown: boolean;
  canClose: boolean;
  additionalBtn?: {
    label: string;
    onClick: () => void;
  };
  onClose: () => void;
}) {
  const isCompactView = useIsCompactView();

  function handleClose() {
    onClose();
  }

  if (!isShown) {
    return null;
  }

  return (
    <AuthModalLayout canClose={canClose} onClose={handleClose}>
      <div className={`w-full h-full overflow-y-auto`}>
        <div
          className={`${
            isCompactView ? "flex-col" : "flex-row min-h-[400px]"
          } w-full h-full flex relative`}
        >
          <div className={`${isCompactView ? "w-full h-[45%]" : "w-[45%]"}`}>
            <div className={`w-full h-full overflow-hidden relative`}>
              <video
                src={blueWave}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className={`${
                  isCompactView && "scale-[1.2]"
                } object-cover w-full h-full`}
              />
              <div
                className={`${
                  isCompactView ? "bg-gradient-to-t" : "bg-gradient-to-l"
                } absolute top-0 bottom-0 left-0 right-0
              from-light-surface-primary dark:from-[#1B1E22] via-[#03142800] 
              to-transparent pointer-events-none`}
              />
            </div>
          </div>

          <div className="absolute left-0 right-0 top-0 bottom-0">
            <Info
              onSignedInWithGoogle={() => onClose()}
              additionalBtn={additionalBtn}
            />
          </div>
        </div>
      </div>
    </AuthModalLayout>
  );
}

function Info({
  additionalBtn,
  onSignedInWithGoogle,
}: {
  additionalBtn?: {
    label: string;
    onClick: () => void;
  };
  onSignedInWithGoogle: () => void;
}) {
  const { signInWithGoogle } = useAuthActions();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await signInWithGoogle();
      onSignedInWithGoogle();
    } catch (error) {
      console.error("Sign in failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex w-full flex-col items-center justify-center 
      h-full px-[5%] py-[10%] gap-10 
    text-[#6B7788] dark:text-dark-text"
    >
      <span className="text-3xl font-[700] text-center">
        Log in or create free account
      </span>
      <span className="text-center font-[600]">
        In order to proceeed you need to be logged in.
      </span>
      <div className="flex flex-col gap-4 w-[258px]">
        <button
          id="auth-modal-google-sign-in"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full h-[40px] bg-[#4285f4] hover:bg-[#357ae8] disabled:opacity-50 disabled:cursor-not-allowed
            text-white font-medium rounded-full flex items-center justify-center gap-3 transition-colors"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <FaGoogle />
              Continue with Google
            </>
          )}
        </button>
        {additionalBtn && (
          <button
            onClick={additionalBtn.onClick}
            className="w-full h-[40px] bg-[#4285f4] hover:bg-[#357ae8] disabled:opacity-50 disabled:cursor-not-allowed
            text-white font-medium rounded-full flex items-center justify-center gap-3 transition-colors"
          >
            {additionalBtn.label}
          </button>
        )}
      </div>
    </div>
  );
}
